import { colorSystem, fontSystem } from '@/styles/theme'
import { HStack, Icon } from '@musinsa/mwms-ds'
import { CSSProperties, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

type Props = {
  headerName: string
  content: React.ReactNode
  position?: 'left' | 'center' | 'right'
}

export function HeaderTooltip({ headerName, content, position = 'center' }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [tooltipCSS, setTooltipCSS] = useState<CSSProperties>({})

  const triggerRef = useRef<HTMLDivElement>(null)

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const tooltipCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !triggerRef.current) return

      const { top, left, width } = triggerRef.current.getBoundingClientRect()
      const tooltipRect = node.getBoundingClientRect()

      let tooltipLeft: number
      let transform: string

      switch (position) {
        case 'left':
          tooltipLeft = left
          transform = 'translate(0, -100%)'
          break
        case 'right':
          tooltipLeft = left + width
          transform = 'translate(-100%, -100%)'
          break
        case 'center':
        default:
          tooltipLeft = left + width / 2
          transform = 'translate(-50%, -100%)'
          break
      }

      // 화면 오른쪽 경계를 벗어나는지 체크
      const tooltipRight = tooltipLeft + (position === 'center' ? tooltipRect.width / 2 : position === 'left' ? tooltipRect.width : 0)
      if (tooltipRight > window.innerWidth) {
        const overflow = tooltipRight - window.innerWidth + 16 // 16px 여백
        tooltipLeft -= overflow
      }

      // 화면 왼쪽 경계를 벗어나는지 체크
      const tooltipLeftEdge = tooltipLeft - (position === 'center' ? tooltipRect.width / 2 : position === 'right' ? tooltipRect.width : 0)
      if (tooltipLeftEdge < 0) {
        tooltipLeft -= tooltipLeftEdge - 16 // 16px 여백
      }

      setTooltipCSS({
        top: top - 8,
        left: tooltipLeft,
        transform,
      })
    },
    [position],
  )

  return (
    <>
      <HStack gap={2} style={{ flex: 1, alignItems: 'center', justifyContent: 'right' }}>
        {headerName}
        <TriggerWrap ref={triggerRef} onMouseEnter={handleOpen} onMouseLeave={handleClose}>
          <Icon name="info" size={16} />
        </TriggerWrap>
      </HStack>
      {isOpen &&
        createPortal(
          <TooltipContent ref={tooltipCallbackRef} style={tooltipCSS} position={position}>
            {content}
          </TooltipContent>,
          document.body,
        )}
    </>
  )
}

const TriggerWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const TooltipContent = styled.div<{ position?: 'left' | 'center' | 'right' }>`
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${colorSystem.bg.common.dim};
  color: #ffffff;
  ${fontSystem.Body_12_medium};
  position: fixed;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${colorSystem.bg.common.dim};

    ${({ position }) => {
      switch (position) {
        case 'left':
          return 'left: 12px;'
        case 'right':
          return 'right: 12px;'
        case 'center':
        default:
          return 'left: 50%; transform: translateX(-50%);'
      }
    }}
  }
`
